import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTabs } from '../TabManager';
import ListPage, { ui } from '../../components/ListPage';
import AccountingSubCoding from './AccountingSubCoding';
import { useFormButtons } from '../../utils/vbPerms';   // سرفصل حساب معین (دابل‌کلیک روی کل)

const toFarsiNum = (str) => String(str).replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const toEngNum   = (str) => String(str).replace(/[۰-۹]/g, d => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d).toString());
const fmt = (n) => (!n && n !== 0) || n === 0 ? '•' : Number(n).toLocaleString('fa-IR');

const ENTITY_OPTS = [{v:0,l:'— انتخاب کنید —'},{v:1,l:'بدهکار'},{v:2,l:'بستانکار'}];
const EMPTY_FORM  = {code:'', name:'', group_id:0, entity:0};

async function apiFetch(path, opts={}) {
  const doFetch = () => fetch(`/api/accounting${path}`, {
    headers:{'Content-Type':'application/json',
              Authorization:`Bearer ${localStorage.getItem('access')}`}, ...opts,
  });
  let res = await doFetch();
  if (res.status === 401) {
    const rr = await fetch('/api/auth/token/refresh/', {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({refresh: localStorage.getItem('refresh')}),
    });
    if (rr.ok) { const d=await rr.json(); localStorage.setItem('access',d.access); res=await doFetch(); }
  }
  return res.json();
}

const FL = ({label, children}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex:1}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    {children}
  </div>
);
const FI = ({name, value, onChange, placeholder}) => (
  <input name={name} value={value} onChange={onChange} placeholder={placeholder||''}
    style={{...ui.input, direction:'rtl', textAlign:'right'}}/>
);
const FSel = ({name, value, onChange, opts}) => (
  <select name={name} value={value} onChange={onChange} style={ui.input}>
    {opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
  </select>
);

export default function AccountingCoding({ user, onBack }) {
  // دکمه‌های فرم FRMACCOUNTINGCODING؛ دیدن = دسترسی
  const vbf = useFormButtons('FRMACCOUNTINGCODING', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK"]);
  const currentYear = new Date().toLocaleDateString('fa-IR').split('/')[0];
  const [year,     setYear]     = useState(currentYear);
  const [data,     setData]     = useState([]);
  const [groups,   setGroups]   = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [meta,     setMeta]     = useState({});
  const [search,   setSearch]   = useState('');
  const [showAll,  setShowAll]  = useState(false);
  const [colW, setColW] = useState([80,100,200,60,120,120,120,120,120,120,130,140]);
  const colWRef = useRef([80,100,200,60,120,120,120,120,120,120,130,140]);
  useEffect(()=>{ colWRef.current = colW; },[colW]);

  const startResize = useCallback((idx, e) => {
    e.preventDefault(); e.stopPropagation();
    const sx = e.clientX, sw = colWRef.current[idx];
    const onMove = ev => { const newW = Math.max(40, sw + (sx - ev.clientX)); setColW(w => { const n=[...w]; n[idx]=newW; return n; }); };
    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
  }, []);
  const [selected, setSelected] = useState(null);
  const [modal,    setModal]    = useState(null);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [saving,   setSaving]   = useState(false);
  const [formErr,  setFormErr]  = useState('');
  const [toast,    setToast]    = useState('');

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),3000); };

  // دابل‌کلیک روی یک «کل» → باز کردن سرفصل حساب معینِ آن
  const { openTab, goHome } = useTabs();
  const openSubCoding = (r) => {
    const coding = { id: r.id, code: r.code, name: r.name, year: toEngNum(year) };
    openTab(`accounting:subcoding:${r.id}`, `معین ${r.code}`, '🧾',
      () => <AccountingSubCoding user={user} onBack={goHome} coding={coding} />);
  };

  const fetchGroups = useCallback(async () => {
    const j = await apiFetch('/coding/groups/');
    if (j.ok) setGroups(j.data);
  }, []);

  const fetchData = useCallback(async (y, sa) => {
    if (!y || y.length !== 4) return;
    setLoading(true); setError('');
    const all = sa !== undefined ? sa : showAll;
    const url = `/api/accounting/coding/?year=${y}&show_all=${all?'1':'0'}`;
    try {
      const res  = await fetch(url, {headers:{Authorization:`Bearer ${localStorage.getItem('access')}`}});
      const json = await res.json();
      if (json.ok) { setData(json.data); setMeta({from:json.date_from, to:json.date_to}); }
      else setError(json.msg || 'خطا');
    } catch { setError('خطا در اتصال'); }
    finally  { setLoading(false); }
  }, [showAll]);

  useEffect(() => { fetchData(year); fetchGroups(); }, []); // eslint-disable-line

  const handleYearChange = e => {
    const v = toEngNum(e.target.value).replace(/[^0-9]/g,'').slice(0,4);
    setYear(v); if (v.length===4) fetchData(v, showAll);
  };

  const filtered = data.filter(r =>
    !search || r.name.includes(search) || String(r.code).includes(search) || r.group_name.includes(search)
  );

  const totals = filtered.reduce((acc,r) => ({
    debit_before:   acc.debit_before   + r.debit_before,
    credit_before:  acc.credit_before  + r.credit_before,
    debit_current:  acc.debit_current  + r.debit_current,
    credit_current: acc.credit_current + r.credit_current,
    debit_total:    acc.debit_total    + r.debit_total,
    credit_total:   acc.credit_total   + r.credit_total,
    remain_debit:   acc.remain_debit   + r.remain_debit,
    remain_credit:  acc.remain_credit  + r.remain_credit,
  }), {debit_before:0,credit_before:0,debit_current:0,credit_current:0,
       debit_total:0,credit_total:0,remain_debit:0,remain_credit:0});

  const openNew = () => { if (!vbf.can('BTNNEW')) return;
    const defaultGroup = selected ? selected.group_id : (groups[0]?.id || 0);
    setForm({...EMPTY_FORM, group_id: defaultGroup}); setFormErr(''); setModal('new');
  };
  const openEdit = () => { if (!vbf.can('BTNEDIT')) return;
    if (!selected) return;
    setForm({ code: String(selected.code || ''), name: selected.name || '',
      group_id: selected.group_id || 0, entity: selected.entity || 0 });
    setFormErr(''); setModal('edit');
  };
  const hf = e => setForm(f=>({...f,[e.target.name]:e.target.value}));

  const save = async () => {
    if (!form.code)       { setFormErr('کد حساب الزامی است');    return; }
    if (!form.name.trim()){ setFormErr('عنوان حساب الزامی است'); return; }
    if (!form.group_id)   { setFormErr('گروه حساب الزامی است');  return; }
    setSaving(true); setFormErr('');
    const payload = {
      year: parseInt(toEngNum(String(year)), 10),
      code: parseInt(toEngNum(String(form.code)), 10) || 0,
      name: form.name.trim(),
      group_id: parseInt(form.group_id, 10) || 0,
      entity: parseInt(form.entity, 10) || 0,
    };
    const j = modal==='new'
      ? await apiFetch('/coding/create/', {method:'POST', body:JSON.stringify(payload)})
      : await apiFetch(`/coding/${selected.id}/update/`, {method:'PUT', body:JSON.stringify(payload)});
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast(j.msg); fetchData(year); }
    else setFormErr(j.msg);
  };

  const doDelete = async () => {
    setSaving(true);
    const j = await apiFetch(`/coding/${selected.id}/delete/`, {method:'DELETE'});
    setSaving(false);
    if (j.ok) { setModal(null); setSelected(null); showToast('حساب حذف شد'); fetchData(year); }
    else { setFormErr(j.msg); setModal('del_blocked'); }
  };

  const groupOpts = [{v:0,l:'— انتخاب گروه —'}, ...groups.map(g=>({v:g.id,l:`${g.code} — ${g.name}`}))];

  const toolbar = (
    <>
      <div style={ui.toolBtns}>
        {vbf.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
        {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...ui.btnEdit,opacity:selected?1:0.4,cursor:selected?'pointer':'default'}}>✎ ویرایش</button>}
        {vbf.can('BTNDELETE') && <button onClick={()=>{if(!selected)return;setFormErr('');setModal('delete');}} disabled={!selected}
          style={{...ui.btnDel,opacity:selected?1:0.4,cursor:selected?'pointer':'default'}}>✕ حذف</button>}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'3px'}}>
        <label style={ui.flabel}>سال مالی</label>
        <input value={toFarsiNum(year)} onChange={handleYearChange} style={ui.yearInput} placeholder="۱۴۰۵" maxLength={4}/>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'3px'}}>
        <label style={ui.flabel}>نمایش</label>
        <label style={{display:'flex',alignItems:'center',gap:'5px',cursor:'pointer',padding:'7px 11px',
          border:'2px solid #e0e0e0',borderRadius:'8px',background:showAll?'#e8f5e9':'#fff',fontSize:'12px',whiteSpace:'nowrap'}}>
          <input type="checkbox" checked={showAll}
            onChange={e=>{setShowAll(e.target.checked);fetchData(year,e.target.checked);}} style={{width:'14px',height:'14px'}}/>
          نمایش همه
        </label>
      </div>
      <input value={search} onChange={e=>setSearch(e.target.value)}
        style={{...ui.searchInput, flex:1, minWidth:'150px'}} placeholder="نام حساب یا کد..."/>
      {loading && <span>⏳</span>}
      {data.length>0 && <div style={ui.countBadge}>{toFarsiNum(String(filtered.length))} از {toFarsiNum(String(data.length))} ردیف</div>}
    </>
  );

  const NUM_BG = '#0a1628';
  return (
    <>
      <ListPage user={user} onBack={onBack} title="سرفصل حساب‌ها" subtitle="تنظیم حساب — سامانه ارجان"
        toolbar={toolbar} error={!modal ? error : ''}>
        {!loading && filtered.length>0 ? (
          <table style={{...ui.table, tableLayout:'fixed', fontSize:'12px'}}>
            <thead style={{position:'sticky',top:0,zIndex:10}}>
              <tr>
                {[
                  {l:'کد',bg:'#1a2f50',tc:'#fff'},{l:'گروه حساب',bg:'#1a2f50',tc:'#fff'},
                  {l:'عنوان حساب',bg:'#1a2f50',tc:'#fff'},{l:'معین',bg:'#1a2f50',tc:'#fff'},
                  {l:'بدهکار قبل',bg:NUM_BG,tc:'#C8A84B'},{l:'بستانکار قبل',bg:NUM_BG,tc:'#C8A84B'},
                  {l:'بدهکار جاری',bg:NUM_BG,tc:'#C8A84B'},{l:'بستانکار جاری',bg:NUM_BG,tc:'#C8A84B'},
                  {l:'بدهکار کل',bg:NUM_BG,tc:'#C8A84B'},{l:'بستانکار کل',bg:NUM_BG,tc:'#C8A84B'},
                  {l:'مانده بدهکار',bg:'#1a3a1a',tc:'#a5d6a7'},{l:'مانده بستانکار',bg:'#3a1a0a',tc:'#ffcc80'},
                ].map((h,i)=>(
                  <th key={i} style={{...ui.th,width:colW[i],minWidth:40,background:h.bg,color:h.tc,position:'relative',userSelect:'none'}}>
                    {h.l}
                    <span onMouseDown={e=>startResize(i,e)} style={{position:'absolute',left:0,top:0,bottom:0,width:'4px',cursor:'col-resize',background:'transparent'}}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r,i)=>{
                const sel = selected?.id===r.id;
                const bg  = sel?'#1a2f50':i%2===0?'#fff':'#fdf6e3';
                const tc  = sel?'#fff':'#333';
                return (
                  <tr key={r.id} style={{background:bg,cursor:'pointer',transition:'background .1s'}}
                    onClick={()=>setSelected(sel?null:r)} onDoubleClick={()=>openSubCoding(r)}
                    title="دابل‌کلیک: حساب‌های معین این کل"
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#e8f0fe';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={{...ui.td,fontFamily:'monospace',fontWeight:'700',color:sel?'#C8A84B':'#1a237e'}}>{r.code}</td>
                    <td style={{...ui.td,color:tc}}>{r.group_name}</td>
                    <td style={{...ui.td,textAlign:'right',fontWeight:'600',color:tc}}>{r.name}</td>
                    <td style={{...ui.td,textAlign:'center',color:sel?'#ccc':'#666'}}>{r.sub_count}</td>
                    <td style={tdNum}>{fmt(r.debit_before)}</td>
                    <td style={tdNum}>{fmt(r.credit_before)}</td>
                    <td style={tdNum}>{fmt(r.debit_current)}</td>
                    <td style={tdNum}>{fmt(r.credit_current)}</td>
                    <td style={{...tdNum,fontWeight:'600'}}>{fmt(r.debit_total)}</td>
                    <td style={{...tdNum,fontWeight:'600'}}>{fmt(r.credit_total)}</td>
                    <td style={{...tdNum,background:sel?'transparent':'#f1f8e9',color:sel?'#a5d6a7':'#1b5e20',fontWeight:'700'}}>{r.remain_debit>0?fmt(r.remain_debit):'•'}</td>
                    <td style={{...tdNum,background:sel?'transparent':'#fff8f0',color:sel?'#ffcc80':'#e65100',fontWeight:'700'}}>{r.remain_credit>0?fmt(r.remain_credit):'•'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{background:'#1a2f50'}}>
                <td colSpan={4} style={{...ui.td,color:'#fff',fontWeight:'700',textAlign:'right',padding:'10px 12px'}}>جمع کل</td>
                {[totals.debit_before,totals.credit_before,totals.debit_current,totals.credit_current].map((v,i)=>(
                  <td key={i} style={{...tdNum,color:'#fff',fontWeight:'700',background:'#1a2f50'}}>{fmt(v)}</td>
                ))}
                {[totals.debit_total,totals.credit_total].map((v,i)=>(
                  <td key={i} style={{...tdNum,color:'#C8A84B',fontWeight:'700',background:'#1a2f50'}}>{fmt(v)}</td>
                ))}
                <td style={{...tdNum,color:'#81c784',fontWeight:'700',background:'#1a2f50'}}>{fmt(totals.remain_debit)}</td>
                <td style={{...tdNum,color:'#ffb74d',fontWeight:'700',background:'#1a2f50'}}>{fmt(totals.remain_credit)}</td>
              </tr>
            </tfoot>
          </table>
        ) : (
          <div style={ui.empty}>
            {loading ? <><div style={{fontSize:'40px',marginBottom:'12px'}}>⏳</div><div>در حال بارگذاری...</div></>
              : <><div style={{fontSize:'48px',marginBottom:'12px'}}>📭</div><div>داده‌ای برای سال {toFarsiNum(year)} یافت نشد</div></>}
          </div>
        )}
      </ListPage>

      {(modal==='new'||modal==='edit') && (
        <div style={ui.overlay} onClick={()=>setModal(null)}>
          <div style={{...ui.modal,maxWidth:'480px'}} onClick={e=>e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal==='new'?'＋ حساب جدید':`✎ ویرایش: ${selected?.name}`}</div>
              <button onClick={()=>setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {formErr && <div style={ui.errBox}>⚠️ {formErr}</div>}
              <div style={{display:'flex',gap:'12px',marginBottom:'14px'}}>
                <div style={{flex:'0 0 120px'}}>
                  <FL label="کد حساب *"><FI name="code" value={toFarsiNum(form.code)} onChange={hf} placeholder="مثلاً ۱۱۰"/></FL>
                </div>
                <div style={{flex:1}}>
                  <FL label="ماهیت حساب"><FSel name="entity" value={form.entity} onChange={hf} opts={ENTITY_OPTS}/></FL>
                </div>
              </div>
              <div style={{marginBottom:'14px'}}>
                <FL label="گروه حساب *"><FSel name="group_id" value={form.group_id} onChange={hf} opts={groupOpts}/></FL>
              </div>
              <div style={{marginBottom:'8px'}}>
                <FL label="عنوان حساب *"><FI name="name" value={form.name} onChange={hf} placeholder="مثلاً موجودی نقد"/></FL>
              </div>
              <div style={{fontSize:'11px',color:'#999',marginTop:'6px'}}>کد تکراری در همین سال مالی ({toFarsiNum(year)}) مجاز نیست</div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={()=>setModal(null)} style={ui.btnCancel}>انصراف</button>
              {vbf.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving?'⏳ ذخیره...':'💾 ذخیره'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal==='delete' && (
        <div style={ui.overlay} onClick={()=>setModal(null)}>
          <div style={{...ui.modal,maxWidth:'440px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...ui.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={ui.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{...ui.mBody,textAlign:'center',padding:'28px'}}>
              <div style={{fontSize:'36px',marginBottom:'12px'}}>🗑️</div>
              <div style={{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'12px',fontWeight:'700',color:'#b71c1c'}}>
                {selected?.name}<br/><span style={{fontSize:'12px',fontWeight:'400'}}>کد: {toFarsiNum(String(selected?.code||''))}</span>
              </div>
              <div style={{fontSize:'12px',color:'#999',marginTop:'10px'}}>اگر این حساب در حساب‌های معین یا اسناد استفاده شده باشد، حذف امکان‌پذیر نیست</div>
            </div>
            <div style={ui.mFooter}>
              <button onClick={()=>setModal(null)} style={ui.btnCancel}>خیر، انصراف</button>
              {vbf.can('BTNDELETE') && <button onClick={doDelete} disabled={saving} style={{...ui.btnSave,background:'#b71c1c'}}>{saving?'⏳...':'بله، حذف شود'}</button>}
            </div>
          </div>
        </div>
      )}

      {modal==='del_blocked' && (
        <div style={ui.overlay} onClick={()=>{setModal(null);setFormErr('');}}>
          <div style={{...ui.modal,maxWidth:'420px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...ui.mHeader,background:'linear-gradient(135deg,#e65100,#f57c00)'}}>
              <div style={ui.mTitle}>🚫 عدم امکان حذف</div>
              <button onClick={()=>{setModal(null);setFormErr('');}} style={ui.closeBtn}>✕</button>
            </div>
            <div style={{...ui.mBody,textAlign:'center',padding:'28px',fontSize:'14px',color:'#1a2f50',fontWeight:'700'}}>{formErr}</div>
            <div style={ui.mFooter}><button onClick={()=>{setModal(null);setFormErr('');}} style={ui.btnSave}>باشه</button></div>
          </div>
        </div>
      )}

      {toast && <div style={ui.toast}>{toast}</div>}
    </>
  );
}

// ستون عددی چپ‌چین (مخصوص این صفحه)
const tdNum = {padding:'7px 12px',borderBottom:'1px solid #f0f0f0',textAlign:'left',
  color:'#1a1a2e',fontFamily:"'Vazirmatn',Tahoma,sans-serif",fontSize:'12px',fontWeight:'600',direction:'ltr'};
